import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AdminUser, AdminRole } from '../types/admin';
import { adminAuth, saveAdminToken, loadAdminToken, debugAdminAuth } from '../services/adminApi';

// 토큰 관련 상수 정의
// 관리자 PWA: 로그인 상태는 localStorage에 저장되며, 명시적 로그아웃 전까지 유지됨 (앱 종료/재실행 후에도 로그인 유지)
const ADMIN_TOKEN_KEY = 'adminToken';
const ADMIN_USER_KEY = 'currentAdmin'; // 기존 코드와 호환성 유지

interface AdminAuthContextType {
  currentAdmin: AdminUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  setCurrentAdmin: React.Dispatch<React.SetStateAction<AdminUser | null>>;
  checkTokenStatus: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType>({
  currentAdmin: null,
  loading: true,
  login: async () => false,
  logout: async () => {},
  isAuthenticated: false,
  setCurrentAdmin: () => {},
  checkTokenStatus: () => {}
});

interface AdminAuthProviderProps {
  children: ReactNode;
}

// 초기 관리자 정보 로드
const getInitialAdmin = (): AdminUser | null => {
  try {
    const adminStr = localStorage.getItem(ADMIN_USER_KEY);
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    
    console.log('🔍 [ADMIN AUTH] 초기 상태 확인:', { hasAdmin: !!adminStr, hasToken: !!token });
    
    // 토큰이 없으면 사용자 정보도 제거
    if (!token) {
      if (adminStr) {
        localStorage.removeItem(ADMIN_USER_KEY);
        console.log('🧹 [ADMIN AUTH] 토큰 없음 - 사용자 정보 제거');
      }
      return null;
    }
    
    if (adminStr) {
      const admin = JSON.parse(adminStr);
      console.log('✅ [ADMIN AUTH] 초기 관리자 정보 로드됨:', admin.username);
      return admin;
    }
    
    return null;
  } catch (error) {
    console.error('❌ [ADMIN AUTH] 초기 상태 로드 오류:', error);
    // 에러 발생 시 localStorage 정리
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    return null;
  }
};

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [currentAdmin, setCurrentAdmin] = useState<AdminUser | null>(getInitialAdmin);
  const [loading, setLoading] = useState(true);

  // 컴포넌트 마운트 시 세션 체크
  useEffect(() => {
    const checkSession = async () => {
      console.log('🔍 [ADMIN AUTH] 세션 체크 시작');
      setLoading(true);

      // Login 페이지에서 impersonate 모드일 때는 세션 체크 건너뛰기
      // (새 창에서 사용자 로그인을 위한 것이므로 관리자 세션 체크 불필요)
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        const urlParams = new URLSearchParams(window.location.search);
        const isImpersonateMode = urlParams.get('impersonate') === 'true';
        
        if (currentPath === '/login' && isImpersonateMode) {
          console.log('🔍 [ADMIN AUTH] Login 페이지 impersonate 모드 - 세션 체크 건너뛰기');
          setCurrentAdmin(null);
          setLoading(false);
          return;
        }
      }

      // 개발 모드에서 Mock 관리자 모드 체크
      const isDevMode = process.env.NODE_ENV === 'development';
      const enableMockAdmin = localStorage.getItem('enableMockAdmin') === 'true';
      
      if (isDevMode && enableMockAdmin) {
        console.log('🛠️ [개발 모드] Mock 관리자 모드 활성화');
        // 더미 관리자 정보 설정
        const mockAdmin: AdminUser = {
          id: 'mock-admin-1',
          username: 'dev-admin',
          email: 'dev-admin@judam.com',
          role: 'super_admin',
          isActive: true,
          lastLoginAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          name: '개발자 관리자'
        };
        setCurrentAdmin(mockAdmin);
        localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(mockAdmin));
        // 더미 토큰도 설정 (API 호출 시 사용)
        if (!loadAdminToken()) {
          saveAdminToken('mock-admin-token-dev-mode');
        }
        setLoading(false);
        console.log('✅ [개발 모드] Mock 관리자 정보 설정 완료');
        return;
      }

      try {
        const token = loadAdminToken();
        
        if (!token) {
          console.log('🔍 [ADMIN AUTH] 토큰 없음 - 로그아웃 상태');
          setCurrentAdmin(null);
          return;
        }

        console.log('🔍 [ADMIN AUTH] 토큰 존재 - 프로필 확인 중...');
        
        try {
          // 프로필 정보 확인으로 토큰 유효성 검증
          const profile = await adminAuth.getProfile();
          console.log('✅ [ADMIN AUTH] 프로필 확인 성공:', profile.username);
          
          setCurrentAdmin(profile);
          localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(profile));
          
        } catch (profileError: any) {
          console.warn('⚠️ [ADMIN AUTH] 프로필 확인 실패:', profileError);
          
          // 401 오류(토큰 만료 등) 시에도 로그아웃하지 않고, 로컬 저장 정보로 세션 유지 (PWA 재실행 시 로그인 유지)
          if (profileError.response?.status === 401) {
            console.log('🔄 [ADMIN AUTH] 401 - 로컬 저장 정보로 세션 유지');
            const storedAdmin = localStorage.getItem(ADMIN_USER_KEY);
            if (storedAdmin) {
              try {
                const admin = JSON.parse(storedAdmin);
                setCurrentAdmin(admin);
                console.log('✅ [ADMIN AUTH] 로컬 저장 정보로 세션 복원 성공:', admin.username);
                return; // 성공 시 early return
              } catch (parseError) {
                console.error('❌ [ADMIN AUTH] 로컬 관리자 정보 파싱 실패:', parseError);
              }
            }
          }
          
          // 네트워크 오류나 서버 오류 시 토큰 유지하고 로컬 정보 사용
          if (!profileError.response || profileError.response.status >= 500) {
            console.log('🔄 [ADMIN AUTH] 서버 오류 또는 네트워크 오류 - 토큰 유지');
            const storedAdmin = localStorage.getItem(ADMIN_USER_KEY);
            if (storedAdmin) {
              try {
                const admin = JSON.parse(storedAdmin);
                setCurrentAdmin(admin);
                console.log('✅ [ADMIN AUTH] 네트워크 오류로 인한 로컬 정보 사용:', admin.username);
                return;
              } catch (parseError) {
                console.error('❌ [ADMIN AUTH] 로컬 관리자 정보 파싱 실패:', parseError);
              }
            }
          }
          
          // 기타 오류 (토큰 만료, 권한 없음 등)인 경우에만 토큰 제거
          console.log('❌ [ADMIN AUTH] 토큰 유효하지 않음 - 로그아웃 처리');
          localStorage.removeItem(ADMIN_TOKEN_KEY);
          localStorage.removeItem(ADMIN_USER_KEY);
          setCurrentAdmin(null);
        }
        
      } catch (error) {
        console.error('❌ [ADMIN AUTH] 예상치 못한 오류:', error);
        setCurrentAdmin(null);
      } finally {
        setLoading(false);
        console.log('🔍 [ADMIN AUTH] 세션 체크 완료');
      }
    };

    checkSession();
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    console.log('🔐 [ADMIN AUTH] 로그인 시작:', username);
    setLoading(true);

    try {
      // 로그인 요청
      const response = await adminAuth.login(username, password);
      console.log('✅ [ADMIN AUTH] 로그인 응답 받음');

      // 토큰 저장
      saveAdminToken(response.token);
      console.log('💾 [ADMIN AUTH] 토큰 저장 완료');

      // 사용자 정보 저장
      setCurrentAdmin(response.user);
      localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(response.user));
      console.log('👤 [ADMIN AUTH] 사용자 정보 저장 완료:', response.user.username);

      return true;
    } catch (error) {
      console.error('❌ [ADMIN AUTH] 로그인 실패:', error);
      
      // 실패 시 정리
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      localStorage.removeItem(ADMIN_USER_KEY);
      setCurrentAdmin(null);
      
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    console.log('🚪 [ADMIN AUTH] 로그아웃 시작');
    setLoading(true);

    try {
      // 서버에 로그아웃 요청
      await adminAuth.logout();
      console.log('✅ [ADMIN AUTH] 서버 로그아웃 완료');
    } catch (error) {
      console.warn('⚠️ [ADMIN AUTH] 서버 로그아웃 실패 (무시):', error);
    }

    // 로컬 상태 정리
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    setCurrentAdmin(null);
    setLoading(false);
    
    console.log('🧹 [ADMIN AUTH] 로컬 상태 정리 완료');
  };

  const checkTokenStatus = () => {
    debugAdminAuth();
  };

  // 인증 상태 계산
  const isAuthenticated = !loading && !!currentAdmin && !!localStorage.getItem(ADMIN_TOKEN_KEY);

  const value = {
    currentAdmin,
    loading,
    login,
    logout,
    isAuthenticated,
    setCurrentAdmin,
    checkTokenStatus
  };

  console.log('🔄 [ADMIN AUTH] Context 값 업데이트:', {
    hasAdmin: !!currentAdmin,
    loading,
    isAuthenticated,
    adminUsername: currentAdmin?.username
  });

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

// 전역 디버깅 함수 등록
if (typeof window !== 'undefined') {
  (window as any).getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);
  (window as any).getAdminInfo = () => localStorage.getItem(ADMIN_USER_KEY);
  (window as any).clearAdminTokens = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    console.log('🧹 [ADMIN AUTH] 토큰 및 정보 제거됨');
  };
}

export default AdminAuthContext;